import React, { Component } from 'react'

import { DebouncedFunc, throttle } from 'lodash'
import { convertRemToPx } from 'shared/utils'

type State = {
  scrollY: number
  oldScrollY: number
  width: number
  height: number
  dir: 'inc' | 'dec'
  borderRadius?: string
  arcDeg: number
}

type Props = {
  animatedProp: 'width' | 'height' | 'arc'
  maxWidth?: number
  maxHeight?: number
  arcDeg?: number
  classes?: string
  styles?: React.CSSProperties
  removeBorderRadius?: boolean
  speed?: number
  bothDirection?: boolean
}

const arcHeight = convertRemToPx(11.125) // высота дуги $arc-height берется из shared/styles/variables

export class ScrollAnimation extends Component<Props, State> {
  ref: React.RefObject<HTMLDivElement> = React.createRef()
  initialWidth = 0
  initialHeight = 0
  handleScrollThrottled: DebouncedFunc<() => void>

  constructor(props: Props) {
    super(props)

    this.state = {
      oldScrollY: 0,
      scrollY: 0,
      width: 0,
      height: 0,
      dir: 'inc',
      arcDeg: this.props?.arcDeg || 0,
    }

    this.handleScrollThrottled = throttle(this.handleScroll, 20)
  }

  componentDidMount(): void {
    window.addEventListener('scroll', this.handleScrollThrottled)
    window.addEventListener('resize', this.handleResize)

    this.setState(this.getInitialValues())
  }

  componentWillUnmount(): void {
    window.removeEventListener('scroll', this.handleScrollThrottled)
    window.removeEventListener('resize', this.handleResize)
  }

  getInitialValues = (): State => {
    this.initialWidth = this.ref?.current?.offsetWidth || 0
    this.initialHeight = this.ref?.current?.offsetHeight || 0

    return {
      ...this.state,
      width: this.initialWidth,
      height: this.initialHeight,
    }
  }

  handleResize = (): void => {
    this.setState({
      ...this.getInitialValues(),
      ...this.calcValues(),
    })
  }

  handleScroll = (): void => {
    const newState = this.calcValues()
    this.setState({ ...newState })
  }

  calcValues = (): State => {
    const rect = this.ref?.current?.getBoundingClientRect() || {
      top: 0,
      bottom: 0,
    }

    if (window.innerHeight - rect.top > 0 && rect.bottom > 0) {
      const diff = window.scrollY - this.state.oldScrollY
      const newScrollY =
        diff !== window.scrollY ? diff * (this.props.speed || 1) : 0
      let newDir = this.state.dir
      let newWidth = this.state.width
      let newHeight = this.state.height
      let newArc = this.state.arcDeg

      if (this.props.bothDirection) {
        if (rect.bottom < window.innerHeight / 2 + 200) {
          newDir = newScrollY >= 0 ? 'dec' : 'inc'
        } else if (rect.top > window.innerHeight / 2) {
          newDir = newScrollY >= 0 ? 'inc' : 'dec'
        }
      } else {
        newDir = newScrollY >= 0 ? 'inc' : 'dec'
      }

      if (newDir === 'inc') {
        const increaseWidth =
          newScrollY >= 0 ? newWidth + newScrollY : newWidth - newScrollY
        const increaseHeight = newHeight + 2
        const decreaseArc = newArc - newScrollY

        const maxWidth = this.props.maxWidth || window.innerWidth
        const maxHeight = this.props.maxHeight || window.innerHeight

        newWidth = increaseWidth > maxWidth ? maxWidth : increaseWidth
        newHeight = increaseHeight > maxHeight ? maxHeight : increaseHeight

        if (rect.top <= -arcHeight) {
          newArc = decreaseArc < 0 ? 0 : decreaseArc
        }
      } else {
        if (window.scrollY === 0) {
          newArc = this.props?.arcDeg || 0
        }

        if (this.props.bothDirection) {
          const decreaseWidth =
            newScrollY >= 0 ? newWidth - newScrollY : newWidth + newScrollY
          newWidth =
            decreaseWidth < this.initialWidth
              ? this.initialWidth
              : decreaseWidth
        }
      }

      const newState: State = {
        ...this.state,
        oldScrollY: window.scrollY,
        scrollY: newScrollY,
        dir: newDir,
      }

      if (this.props.animatedProp === 'width') {
        newState.width = newWidth
      } else if (this.props.animatedProp === 'height') {
        newState.height = newHeight
      } else {
        newState.arcDeg = newArc
      }
      return newState
    } else {
      return {
        ...this.state,
        width: this.props?.bothDirection ? this.initialWidth : this.state.width,
        arcDeg: 0,
      }
      // this.setState({
      //   width: this.props?.bothDirection ? this.initialWidth : this.state.width,
      //   arcDeg: 0,
      // })
    }
  }

  calcBorderRadius = (): string => {
    const { animatedProp, removeBorderRadius } = this.props
    const { width, arcDeg } = this.state
    const isArc = animatedProp === 'arc'

    if (width === window.innerWidth && removeBorderRadius && !isArc) {
      return '0'
    }

    if (isArc) {
      return `0 0 50% 50% / ${arcDeg}px`
    }

    return ''
  }

  render(): JSX.Element {
    const { classes, children, animatedProp, styles } = this.props
    const { width, height } = this.state
    const isWidth = animatedProp === 'width'
    const isHeight = animatedProp === 'height'
    const _styles = {
      width: width > 0 && isWidth ? `${width}px` : '',
      minWidth: isWidth ? `${this.initialWidth}px` : '',
      height: height > 0 && isHeight ? `${height}px` : '',
      minHeight: isHeight ? `${this.initialHeight}px` : '',
      borderRadius: this.calcBorderRadius(),
      ...styles,
    }

    return (
      <div className={classes} ref={this.ref} style={_styles}>
        {children}
      </div>
    )
  }
}
